/**
 * Baby cost calculator — pure accrual engine.
 *
 * Turns a window (start..end month) + a list of items (consumables, services,
 * one-time costs) into monthly cumulative + per-month cost series.
 *
 * Conventions (see docs/baby/SCOPE.md anti-regression rules):
 *  - 30.4375 days/month (365.25/12) for month↔day conversion.
 *  - Active ranges are [startMonth, endMonth): a 10→20 item covers months
 *    10..19 inclusive (10 active months). Inclusive start, exclusive end —
 *    matches array-slice semantics and avoids off-by-one at the boundary.
 *  - Costs are non-negative; inputs are clamped at 0.
 *  - All pure: no DOM, no Chart.js, no side effects.
 */

import type {
  BabyCalcInput,
  BabyCalcResult,
  BabyItem,
  BabyItemKind,
  BabyItemSeries,
  BabyRatePeriod,
} from '../shared/types';

/** Average days per month — 365.25 / 12. Used for week/month → day conversion. */
export const DAYS_PER_MONTH = 30.4375;
/** Days per week, for rate conversion. */
export const DAYS_PER_WEEK = 7;

/**
 * Convert a usage rate to "units per day".
 *  - 'day':   rate as-is
 *  - 'week':  rate / 7
 *  - 'month': rate / 30.4375
 */
export function ratePerDay(rate: number, period: BabyRatePeriod): number {
  if (rate <= 0) return 0;
  switch (period) {
    case 'day':
      return rate;
    case 'week':
      return rate / DAYS_PER_WEEK;
    case 'month':
      return rate / DAYS_PER_MONTH;
    default:
      return 0;
  }
}

/** Average weeks per month = 52 / 12 ≈ 4.348. */
export const WEEKS_PER_MONTH = 52 / 12;

/**
 * Convert a usage rate to "units per month" — used by the service branch so a
 * service can be quoted per day (e.g. Ontario's $22/day CWELCC cap), per week,
 * or per month.
 *  - 'day':   rate × 30.4375
 *  - 'week':  rate × 4.348
 *  - 'month': rate as-is
 */
export function ratePerMonth(rate: number, period: BabyRatePeriod): number {
  if (rate <= 0) return 0;
  switch (period) {
    case 'day':
      return rate * DAYS_PER_MONTH;
    case 'week':
      return rate * WEEKS_PER_MONTH;
    case 'month':
      return rate;
    default:
      return 0;
  }
}

/**
 * Compute the per-month cost (CAD) of a single item across the window.
 *
 *  - **consumable**: unitCost × (rate→per-day) × days-in-month, for each active
 *    month. Active months are [max(start, windowStart), min(end, windowEnd)).
 *  - **service**: monthly charge = unitCost × rate × (period→months), charged
 *    each active month. Supports per-day ($22/day), per-week, or per-month quoting.
 *  - **onetime**: unitCost lands entirely in the single month `startMonth`
 *    (clamped into the window); all other months are 0.
 *
 * Returns an array of length `windowMonths` (one entry per month in the window).
 * The result is the *period* cost (not cumulative).
 */
export function itemPerMonth(item: BabyItem, windowMonths: number, windowStart: number): number[] {
  const perMonth = new Array<number>(windowMonths).fill(0);

  if (!item.enabled) return perMonth;

  const cost = Math.max(0, item.unitCost);
  if (cost <= 0) return perMonth;

  // Effective active range, clamped to the window. [startInWindow, endInWindow).
  const startInWindow = Math.max(item.startMonth, windowStart);
  const endRaw = item.endMonth < 0 ? Number.POSITIVE_INFINITY : item.endMonth;
  const endInWindow = Math.min(endRaw, windowStart + windowMonths);

  if (item.kind === 'onetime') {
    // The cost lands in `item.startMonth`, if it falls inside the window.
    const landIdx = item.startMonth - windowStart;
    if (landIdx >= 0 && landIdx < windowMonths) {
      perMonth[landIdx] = cost;
    }
    return perMonth;
  }

  if (startInWindow >= endInWindow) return perMonth;

  if (item.kind === 'service') {
    // Monthly charge = unitCost × rate × (period→months factor). This lets a
    // service be quoted per day ($22/day Ontario cap), per week, or per month.
    // Default rate=1 + period='month' preserves the original "$X/month" model.
    const chargesPerMonth = ratePerMonth(item.rate, item.ratePeriod);
    if (chargesPerMonth <= 0) return perMonth;
    const monthly = cost * chargesPerMonth;
    for (let m = startInWindow; m < endInWindow; m++) {
      const idx = m - windowStart;
      if (idx >= 0 && idx < windowMonths) perMonth[idx] = monthly;
    }
    return perMonth;
  }

  // consumable: unitCost × per-day-rate × days-in-this-month.
  const perDay = ratePerDay(item.rate, item.ratePeriod);
  if (perDay <= 0) return perMonth;
  for (let m = startInWindow; m < endInWindow; m++) {
    const idx = m - windowStart;
    if (idx >= 0 && idx < windowMonths) {
      perMonth[idx] = cost * perDay * DAYS_PER_MONTH;
    }
  }
  return perMonth;
}

/** Convert a per-month array to a cumulative (running-sum) array of length n+1. */
export function cumulative(perMonth: number[]): number[] {
  const out = new Array<number>(perMonth.length + 1).fill(0);
  let run = 0;
  for (let i = 0; i < perMonth.length; i++) {
    run += perMonth[i]!;
    out[i + 1] = run;
  }
  return out;
}

/**
 * Full calculation: window + items → per-item cumulative series + stacked total.
 *
 * The total is the elementwise sum of every *enabled* item's cumulative series,
 * so it can be plotted as a stacked line (each item stacks on the previous).
 */
export function calculateBabyCosts(input: BabyCalcInput): BabyCalcResult {
  const windowStart = Math.max(0, Math.floor(input.windowStartMonth));
  const windowEnd = Math.max(windowStart + 1, Math.floor(input.windowEndMonth));
  const windowMonths = windowEnd - windowStart;

  const monthLabels: number[] = [];
  for (let m = windowStart; m <= windowEnd; m++) monthLabels.push(m);

  const series: BabyItemSeries[] = [];
  const total = new Array<number>(windowMonths + 1).fill(0);

  for (const item of input.items) {
    if (!item.enabled) continue;
    const perMonth = itemPerMonth(item, windowMonths, windowStart);
    const cum = cumulative(perMonth);
    series.push({ id: item.id, label: item.label, cumulative: cum, perMonth });
    for (let i = 0; i < total.length; i++) {
      const v = cum[i];
      if (typeof v === 'number') {
        total[i] = (total[i] ?? 0) + v;
      }
    }
  }

  return {
    windowMonths,
    monthLabels,
    series,
    total,
    grandTotal: total[total.length - 1]!,
  };
}

/**
 * Sum the cumulative series for a specific item id (helper for tests / UI).
 * Returns null if the item is disabled or absent.
 */
export function itemCumulativeTotal(item: BabyItem, windowMonths: number, windowStart: number): number {
  if (!item.enabled) return 0;
  const perMonth = itemPerMonth(item, windowMonths, windowStart);
  return perMonth.reduce((a, b) => a + b, 0);
}

/* ----------------------- URL state serialization ------------------------ */

const VALID_KINDS: ReadonlySet<BabyItemKind> = new Set(['consumable', 'service', 'onetime']);
const VALID_PERIODS: ReadonlySet<BabyRatePeriod> = new Set(['day', 'week', 'month']);

/**
 * Serialize a list of items into a URL-safe string (URL-encoded JSON) for the
 * shareable link. Trims to the fields needed to reconstruct state. Pure.
 */
export function encodeItems(list: BabyItem[]): string {
  const slim = list.map((it) => ({
    id: it.id,
    label: it.label,
    kind: it.kind,
    enabled: it.enabled,
    unitCost: it.unitCost,
    rate: it.rate,
    ratePeriod: it.ratePeriod,
    startMonth: it.startMonth,
    endMonth: it.endMonth,
    custom: it.custom,
    source: it.source,
    help: it.help,
    details: it.details,
  }));
  return encodeURIComponent(JSON.stringify(slim));
}

/**
 * Parse a string produced by `encodeItems` back into a BabyItem[].
 * Returns null if the input is absent or malformed — caller falls back to
 * defaults. Validates the shape of every item; rejects obvious garbage so a
 * tampered URL can't crash the calculator. Pure.
 */
export function decodeItems(param: string | undefined | null): BabyItem[] | null {
  if (!param) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(decodeURIComponent(param));
  } catch {
    return null;
  }
  if (!Array.isArray(parsed)) return null;
  const out: BabyItem[] = [];
  for (const raw of parsed) {
    if (typeof raw !== 'object' || raw === null) return null;
    const it = raw as Record<string, unknown>;
    if (
      typeof it.id !== 'string'
      || typeof it.label !== 'string'
      || typeof it.kind !== 'string'
      || !VALID_KINDS.has(it.kind as BabyItemKind)
      || typeof it.ratePeriod !== 'string'
      || !VALID_PERIODS.has(it.ratePeriod as BabyRatePeriod)
      || typeof it.unitCost !== 'number'
      || typeof it.rate !== 'number'
      || typeof it.startMonth !== 'number'
      || typeof it.endMonth !== 'number'
      || typeof it.enabled !== 'boolean'
      || typeof it.custom !== 'boolean'
    ) {
      return null;
    }
    out.push({
      id: it.id,
      label: it.label,
      kind: it.kind as BabyItemKind,
      enabled: it.enabled,
      unitCost: it.unitCost,
      rate: it.rate,
      ratePeriod: it.ratePeriod as BabyRatePeriod,
      startMonth: it.startMonth,
      endMonth: it.endMonth,
      custom: it.custom,
      source: typeof it.source === 'string' ? it.source : undefined,
      help: typeof it.help === 'string' ? it.help : undefined,
      details: typeof it.details === 'string' ? it.details : undefined,
    });
  }
  return out;
}

/* ----------------------- small-series grouping ------------------------- */

/**
 * A chart series after small-series grouping. Identical to BabyItemSeries but
 * carries the list of original items folded into an "Other" bucket, for the
 * tooltip breakdown. Named series have `members` = [themselves].
 */
export interface GroupedSeries extends BabyItemSeries {
  /** Original item ids+labels folded into this band. Length 1 for named bands. */
  members: Array<{ id: string; label: string; total: number }>;
  /** True for the synthetic "Other" bucket. */
  isOther: boolean;
}

export interface GroupedCalcResult {
  windowMonths: number;
  monthLabels: number[];
  series: GroupedSeries[];
  total: number[];
  grandTotal: number;
}

/**
 * Default cap on visible bands. UK Gov Analysis Function recommends ≤4 stacks,
 * ONS ≤5 segments — 5 (4 named + 1 "Other") satisfies both. Source:
 * https://analysisfunction.civilservice.gov.uk/support/communicating-analysis/introduction-to-data-visualisation-e-learning/module-7-stacked-bar-charts/
 * https://service-manual.ons.gov.uk/data-visualisation/chart-types/stacked-bar-charts
 */
export const DEFAULT_MAX_BANDS = 5;

/**
 * Threshold below which a series is folded into "Other", as a fraction of the
 * grand total. 3% is the common practitioner cutoff; below this a band is too
 * thin to read in a stacked area chart.
 */
export const SMALL_SERIES_FRACTION = 0.03;

/**
 * Fold small series into a single "Other" band so the chart stays legible.
 *
 * Rule (see DEFAULT_MAX_BANDS / SMALL_SERIES_FRACTION docs):
 *  1. Compute each series' share of the grand total (by final cumulative value).
 *  2. Keep the largest series that are ≥ SMALL_SERIES_FRACTION, up to
 *     MAX_BANDS - 1 of them (reserving one slot for "Other").
 *  3. Fold the rest into one "Other" series (summed elementwise), carrying the
 *     member labels for the tooltip.
 *  4. If nothing is small enough to fold, return the series unchanged (no
 *     "Other" band created) — never manufacture an empty bucket.
 *
 * Order of the returned bands is largest-first (the biggest cost sits on the
 * baseline, per stacked-chart best practice), with "Other" last.
 *
 * Pure — no DOM, no side effects.
 */
export function groupSmallSeries(
  result: BabyCalcResult,
  maxBands: number = DEFAULT_MAX_BANDS,
  smallFraction: number = SMALL_SERIES_FRACTION,
): GroupedCalcResult {
  const { windowMonths, monthLabels, total, grandTotal } = result;

  // Rank series by their final cumulative value (descending).
  const ranked = [...result.series].sort(
    (a, b) => (b.cumulative[b.cumulative.length - 1] ?? 0) - (a.cumulative[a.cumulative.length - 1] ?? 0),
  );

  // Nothing to group if we're already at/under the cap.
  if (ranked.length <= maxBands) {
    return {
      windowMonths,
      monthLabels,
      total,
      grandTotal,
      series: ranked.map((s) => ({
        ...s,
        members: [{ id: s.id, label: s.label, total: s.cumulative[s.cumulative.length - 1] ?? 0 }],
        isOther: false,
      })),
    };
  }

  // Reserve one slot for "Other"; keep the top (maxBands - 1) named bands.
  const keepCount = maxBands - 1;
  const kept = ranked.slice(0, keepCount);
  const folded = ranked.slice(keepCount);

  // Only actually fold if at least one folded series is "small". If all the
  // would-be-folded series are individually significant (≥ smallFraction), the
  // count cap is doing the work — still fold, but this guard avoids a single
  // trivial series being lumped. We always fold when count > maxBands.
  void smallFraction; // threshold documented; the count cap drives the fold here

  // Sum the folded series elementwise into the "Other" band.
  const len = total.length;
  const otherCumulative = new Array<number>(len).fill(0);
  const otherPerMonth = new Array<number>(Math.max(0, len - 1)).fill(0);
  const members: Array<{ id: string; label: string; total: number }> = [];
  for (const s of folded) {
    for (let i = 0; i < len; i++) otherCumulative[i] = (otherCumulative[i] ?? 0) + (s.cumulative[i] ?? 0);
    for (let i = 0; i < s.perMonth.length; i++) otherPerMonth[i] = (otherPerMonth[i] ?? 0) + (s.perMonth[i] ?? 0);
    members.push({ id: s.id, label: s.label, total: s.cumulative[s.cumulative.length - 1] ?? 0 });
  }

  const series: GroupedSeries[] = kept.map((s) => ({
    ...s,
    members: [{ id: s.id, label: s.label, total: s.cumulative[s.cumulative.length - 1] ?? 0 }],
    isOther: false,
  }));
  if (folded.length > 0) {
    series.push({
      id: '__other__',
      label: 'Other',
      cumulative: otherCumulative,
      perMonth: otherPerMonth,
      members,
      isOther: true,
    });
  }

  return { windowMonths, monthLabels, total, grandTotal, series };
}


